import { useState } from 'react';
import type { SavedView } from '../components/SavedViewsDropdown';
import { callListViews } from '../data/contactCallListViews';
import type { ContactListView } from '../data/contactCallListViews';

const ALL_CONTACTS: SavedView = {
  id: 'all',
  name: 'All Contacts',
  type: 'default'
};

interface UseContactViewsOptions {
  /** Applies a view's filters and sort to the table; null clears everything */
  onApply: (view: ContactListView | null) => void;
}

// Contacts table views: "All Contacts", preset call lists, and views saved by staff
export function useContactViews({
  onApply
}: UseContactViewsOptions) {
  const [currentView, setCurrentView] = useState(ALL_CONTACTS.name);
  const [customViews, setCustomViews] = useState<(ContactListView & {
    isShared: boolean;
  })[]>([]);

  const savedViews: SavedView[] = [ALL_CONTACTS, ...callListViews.map(view => ({
    id: view.id,
    name: view.name,
    type: 'preset' as const,
    group: 'Call lists'
  })), ...customViews.map(view => ({
    id: view.id,
    name: view.name,
    type: view.isShared ? 'team' as const : 'mine' as const,
    creator: view.isShared ? 'You' : undefined,
    isShared: view.isShared
  }))];

  const findView = (id: string) => [...callListViews, ...customViews].find(view => view.id === id) ?? null;

  const selectView = (view: SavedView) => {
    setCurrentView(view.name);
    onApply(view.type === 'default' ? null : findView(view.id));
  };

  const saveView = (name: string, isShared: boolean, filters: Record<string, string[]>, sort: ContactListView['sort']) => {
    const id = `custom-${Date.now()}`;
    setCustomViews(prev => [...prev, {
      id,
      name,
      filters,
      sort,
      isShared
    }]);
    setCurrentView(name);
  };

  const deleteView = (id: string) => {
    setCustomViews(prev => prev.filter(view => view.id !== id));
    if (customViews.find(view => view.id === id)?.name === currentView) setCurrentView(ALL_CONTACTS.name);
  };

  const duplicateView = (id: string) => {
    const source = findView(id);
    if (!source) return;
    setCustomViews(prev => [...prev, {
      ...source,
      id: `custom-${Date.now()}`,
      name: `${source.name} (copy)`,
      isShared: false
    }]);
  };

  const renameView = (id: string, name: string) => setCustomViews(prev => prev.map(view => view.id === id ? {
    ...view,
    name
  } : view));

  // Any manual change to filters takes the table off its named view
  const resetToAll = () => setCurrentView(ALL_CONTACTS.name);

  return {
    currentView,
    savedViews,
    selectView,
    saveView,
    deleteView,
    duplicateView,
    renameView,
    resetToAll
  };
}
